/**
 * Minimal className combiner for the Dispatch Deck component set.
 * Deliberately dependency-free (no clsx): variants in this codebase are
 * plain string unions resolved via `Record` lookups, so all we ever
 * need is filtering falsy segments.
 */
export function cx(...parts: ReadonlyArray<string | false | null | undefined>): string {
  const out: string[] = [];
  for (const part of parts) {
    if (typeof part === 'string' && part.length > 0) {
      out.push(part);
    }
  }
  return out.join(' ');
}

/**
 * True when `className` sets an UNPREFIXED width utility (`w-56`,
 * `w-full`, `w-1/2`, …). Field components use this to decide whether to
 * emit their default `w-full`: Tailwind emits `.w-full` after the
 * numbered widths, so a hardcoded base `w-full` would win by source
 * order and silently defeat a caller's plain `w-56` — the base must be
 * dropped in that case.
 *
 * Breakpoint/state-prefixed widths (`lg:w-52`, `hover:w-64`) are
 * deliberately NOT matched: those are conditional overrides that should
 * layer on top of the `w-full` default (Tailwind orders responsive
 * variants after base utilities, so `lg:w-52` wins at `lg`+ while the
 * base keeps a sane full-width fallback below the breakpoint). The
 * token-start anchor also excludes `min-w-*` / `max-w-*`.
 */
export function hasWidthClass(className: string | undefined): boolean {
  return /(?:^|\s)w-/.test(className ?? '');
}
